import refreshSession from 'lib/refreshSession';
import AuthData, { purgeLegacyAuthData } from 'lib/authData';
import { shouldProbeSession, clearSessionMarker } from 'lib/sessionMarker';
import { currentSessionGeneration } from 'lib/sessionGeneration';
import { notifyAuthChanged } from 'lib/authChange';
import AuthenticationActions from 'actions/communication/authentication';
import DataStoreActions from 'actions/dataStore';

/**
 * How long first paint may be blocked on the session probe.
 *
 * A probe that never settles — an unreachable API, a hanging proxy — would
 * otherwise leave the loading shell on screen forever, because `index.js` calls
 * `ReactDOM.render` inside `configureApp`'s callback (AC22).
 */
export const BOOT_PROBE_TIMEOUT_MS = 10000;

type Store = { dispatch: (action: unknown) => unknown };

/** Exceptions that mean "this browser has no session", as opposed to "we could not tell". */
const NO_SESSION_ERRORS = ['UnauthorizedException', 'AuthTokenExpiredException'];

const hasCachedUser = (): boolean => {
  try {
    return Boolean((AuthData.get() as { user?: unknown } | undefined)?.user);
  } catch (_e) {
    return false;
  }
};

const isNoSession = (error: unknown): boolean =>
  NO_SESSION_ERRORS.includes((error as { name?: string })?.name ?? '');

/**
 * CD-8740 — resolve the user's session from the server at boot.
 *
 * Replaces the old `localStorage` read: `PUT /sessions` succeeds if the browser
 * holds a valid `access_refresh` cookie and returns the user, or 401s if not.
 *
 * The returned promise **always resolves**, and always within
 * `BOOT_PROBE_TIMEOUT_MS`. `configureApp` gates the whole app render on it, so a
 * rejection or a hang here is a permanently blank page.
 *
 * The timeout bounds first paint, **not the probe**. When it fires the app
 * renders, but the probe keeps running and applies its result late if it ever
 * settles: a late success stores the user and dispatches `updateToken()`, a late
 * 401 clears the marker and cached profile and dispatches `dropAll`. That is
 * deliberate — on a slow network the session is still restored, rather than the
 * user being left signed out until they reload. Since the app has already
 * painted from the old profile, a late result that changes the profile also
 * announces it (`notifyAuthChanged`) so the guarded screens re-render: a late
 * success moves the user off the sign-in / anonymous screen, a late 401 off a
 * private page. An in-time result announces nothing; first paint reads it.
 *
 * But a result is only applied if it still describes the session the probe
 * started in. If the user has signed in or out meanwhile (the login saga
 * advances the session generation), the late result is dropped: a late 401
 * would wipe the new session, a late success would start a second refresh loop
 * or undo the sign-out.
 *
 * Failure handling is deliberately asymmetric:
 *
 * - **401** — the server has told us there is no session. Clear the cached
 *   profile and the stale marker, and render the unauthenticated shell.
 * - **Anything else** (network error, 5xx, timeout) — we could not tell. Leave
 *   the cached profile and the marker exactly as they are and render. Signing a
 *   user out because their train went into a tunnel is the failure mode this
 *   whole design exists to avoid; a real 401 on the next request routes through
 *   the normal handler. A kept profile still needs its hourly refresh, so the
 *   loop is started for it just as after a success.
 */
const bootSessionProbe = (store: Store, timeoutMs: number = BOOT_PROBE_TIMEOUT_MS): Promise<void> => {
  // Runs first, so a pre-cutover entry cannot influence the probe decision.
  purgeLegacyAuthData();

  if (!shouldProbeSession()) return Promise.resolve();

  let timer: ReturnType<typeof setTimeout>;
  // Once true the app has painted without waiting for us, so whatever the probe
  // changes from here on needs announcing (`notifyAuthChanged`); before that,
  // first paint reads the result itself.
  let paintedWithoutUs = false;
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => {
      paintedWithoutUs = true;
      resolve('timeout');
    }, timeoutMs);
  });

  // The probe owns its own failure. Without this the globally configured
  // `onError` would push /unauthorized on a stale marker, before the app has
  // rendered — a redirect for what is simply an anonymous visit.
  //
  // `refreshSession`, not a bare `sessionsUpdate`: refresh is a single-use
  // rotation, and N tabs restored together all boot-probe at once. The wrapper
  // serialises them cross-tab and lets late tabs reuse the first tab's result
  // instead of losing the rotation race and rendering signed out.
  const startedIn = currentSessionGeneration();
  const stale = () => currentSessionGeneration() !== startedIn;

  const probe = refreshSession({ onError: (error: unknown) => Promise.reject(error) })
    .then((result: { user?: unknown }) => {
      if (stale()) return;

      AuthData.store(result);
      store.dispatch(AuthenticationActions.updateToken());
      if (paintedWithoutUs) notifyAuthChanged();
    })
    .catch((error: unknown) => {
      if (stale()) return;

      if (!isNoSession(error)) {
        if (hasCachedUser()) store.dispatch(AuthenticationActions.updateToken());
        return;
      }

      clearSessionMarker();
      try {
        AuthData.drop();
      } catch (_e) {
        // localStorage unavailable — nothing cached to clear.
      }
      store.dispatch(DataStoreActions.dropAll());
      if (paintedWithoutUs) notifyAuthChanged();
    });

  return Promise.race([probe, timeout]).then(() => {
    clearTimeout(timer);
  });
};

export default bootSessionProbe;
